import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiLinks } from '@cutting/app.api-links';
import { FuseConfigService } from '@fuse/services/config.service';
import { CuttingSettings } from 'cloud-shared-lib';
import { tap } from 'rxjs';

/**
 * Displays maintenance page and handles navigation after maintenance is complete.
 * Automatically redirects to the original URL (stored in redirectUrl query param)
 * when maintenance mode is disabled.
 */
@Component({
  selector: 'cutting-maintenance',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="absolute inset-0 z-[9999] flex flex-col items-center justify-center min-h-screen bg-white dark:bg-gray-900">
      <div
        i18n
        class="mt-12 sm:mt-24 text-4xl md:text-7xl font-extrabold tracking-tight leading-tight md:leading-none text-center"
        >Проводятся технические работы</div
      >
      <div i18n class="mt-2 text-lg md:text-xl font-medium tracking-tight text-center text-secondary"
        >Скоро все заработает - обязательно возвращайтесь!</div
      >
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceComponent {
  private readonly fuseConfig = inject(FuseConfigService);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isMaintenance = signal(true);
  private readonly defaultRedirect = '/';

  constructor() {
    this.setupConfigEffect();
    this.checkMaintenanceStatus();
  }

  private setupConfigEffect(): void {
    effect(() => {
      this.fuseConfig.config = {
        layout: {
          toolbar: { hidden: this.isMaintenance() },
        },
      };
    });
  }

  private checkMaintenanceStatus(): void {
    this.http
      .get<CuttingSettings>(ApiLinks.cuttingSettings)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(settings => {
          this.isMaintenance.set(settings.isMaintenanceInProgress);
          if (!settings.isMaintenanceInProgress) {
            const redirectUrl = this.route.snapshot.queryParams['redirectUrl'] || this.defaultRedirect;
            this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
          }
        })
      )
      .subscribe();
  }
}
